import apiClient from './apiClient/apiClient';
import { Store, StoreResponse } from '../types';
import { MenusAPIResponse, MenuAPIStatus } from '../types/menuApi';
import { promisePool } from '../utils/promisePool';
import { StoreControlResponse } from './storeControl/storeControl.types';

// Get menu publish job status
export const getMenuJobStatus = async (jobId: string): Promise<MenuJobStatusResponse> => {
  const response = await apiClient.get(`/publish/jobs/${jobId}`);
  return response.data;
};
// Menu job status response type for menu publish jobs
export interface MenuJobStatusResponse {
  jobId: string;
  status: string;
  totalSubJobs: number;
  totalSubJobsCompleted: number;
  totalSubJobsFailed: number;
  totalSubJobsInProgress: number;
  startedAt?: string;
  completedAt?: string;
  [key: string]: any;
}

// Get all bundles
export const getAllBundles = async (): Promise<any[]> => {
  let all: any[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get('/api/bundles', { params: { page, pageSize } });
    const data = response.data;
    let items = Array.isArray(data) ? data : (data && Array.isArray(data.bundles) ? data.bundles : []);
    all = all.concat(items);
    pages = data.pages || 1;
    page++;
  }
  return all;
};

// Get all modifiers
export const getAllModifiers = async (): Promise<any[]> => {
  let all: any[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get('/api/modifiers', { params: { page, pageSize } });
    const data = response.data;
    let items = Array.isArray(data) ? data : (data && Array.isArray(data.modifiers) ? data.modifiers : []);
    all = all.concat(items);
    pages = data.pages || 1;
    page++;
  }
  return all;
};

// Get all option types
export const getAllOptionTypes = async (): Promise<any[]> => {
  let all: any[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get('/api/option-types', { params: { page, pageSize } });
    const data = response.data;
    let items = Array.isArray(data) ? data : (data && Array.isArray(data.optionTypes) ? data.optionTypes : []);
    all = all.concat(items);
    pages = data.pages || 1;
    page++;
  }
  return all;
};

// Get all option types and return a map of value codes to names
export const getAllOptionTypesWithValues = async (): Promise<Record<string, string>> => {
  const map: Record<string, string> = {};
  let allOptionTypes: any[] = [];
  let page = 0;
  const pageSize = 100;
  let total = 0;

  do {
    const res = await apiClient.get('/api/option-types', { params: { page, pageSize } });
    const data = res.data;
    if (Array.isArray(data.optionTypes)) {
      allOptionTypes = allOptionTypes.concat(data.optionTypes);
    }
    total = data.count || 0;
    page++;
  } while (allOptionTypes.length < total);

  allOptionTypes.forEach((type: any) => {
    if (Array.isArray(type.values)) {
      type.values.forEach((val: any) => {
        if (val.optionValueCode && val.name) {
          map[val.optionValueCode] = val.name;
        }
      });
    }
  });

  return map;
};

// Legacy job status types (keep for compatibility)
export enum PatchJobStatus {
  QUEUED = 'QUEUED',
  STARTED = 'STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

// Define menu job status types
export enum MenuJobStatus {
  QUEUED = 'QUEUED',
  STARTED = 'STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

// Get active menus for selected stores with new response structure handling
export const getActiveMenus = async (storeNumbers: string[], page = 0, pageSize = 5): Promise<MenusAPIResponse> => {
  try {
    const response = await apiClient.post('/api/menu/active', {
      storeNumbers,
      page,
      pageSize,
    });

    // Handle different status codes
    switch (response.status) {
      case MenuAPIStatus.SUCCESS:
        return response.data;
        
      case MenuAPIStatus.PARTIAL_SUCCESS:
        return response.data;
        
      case MenuAPIStatus.SERVER_ERROR:
        throw new Error(`Server error: ${response.data?.failed?.[0]?.message || 'Unknown error'}`);
        
      default:
        return response.data;
    }
  } catch (error: any) {
    // Handle network errors or unexpected responses
    if (error.response?.status === MenuAPIStatus.BAD_REQUEST) {
      throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request parameters'}`);
    }
    throw error;
  }
};

// Get inactive menus for selected stores with new response structure handling
export const getInactiveMenus = async (storeNumbers: string[], page = 0, pageSize = 5): Promise<MenusAPIResponse> => {
  try {
    const response = await apiClient.post('/api/menu/inactive', {
      storeNumbers,
      page,
      pageSize,
    });

    // Handle different status codes
    switch (response.status) {
      case MenuAPIStatus.SUCCESS:
        return response.data;
        
      case MenuAPIStatus.PARTIAL_SUCCESS:
        return response.data;
        
      case MenuAPIStatus.SERVER_ERROR:
        throw new Error(`Server error: ${response.data?.failed?.[0]?.message || 'Unknown error'}`);
        
      default:
        return response.data;
    }
  } catch (error: any) {
    // Handle network errors or unexpected responses
    if (error.response?.status === MenuAPIStatus.BAD_REQUEST) {
      throw new Error(`Invalid request: ${error.response.data?.message || 'Bad request parameters'}`);
    }
    throw error;
  }
};

// Get inactive menu UUIDs for selected stores
export const getInactiveMenuIds = async (storeNumbers: string[]): Promise<any> => {
  const response = await apiClient.post('/api/menu/inactive/id', {
    storeNumbers,
  });
  return response.data;
};

// Refresh store cache
export const refreshStoreCache = async (storeNumbers: string[]): Promise<any> => {
  // Use endpoint for refreshing cache for specific stores by id
  const response = await apiClient.post('/api/cache/refresh/storesbyid', {
    storeNumbers,
  });
  return response.data;
};

// --- Types for Bulk Update ---
export interface ModifierWeight {
  modifierWeightId: string;
  modifierWeightCode: string;
  name: string;
}

export interface Slot {
  slotId: string;
  slotCode: string;
  name: string;
}

export interface Variant {
  variantId: string;
  selectedOptions: Array<{ optionValue: { internalName: string } }>;
}

export interface PatchJobRequest {
  patches: any[];
  storeNumbers: string[];
  createdBy: string;
}

export interface JobResponse {
  jobId: number;
  status: string;
  message?: string;
}

export interface JobsResponse {
  jobs: JobResponse[];
}

export interface PatchJobStatusResponse {
  success: boolean;
  jobId: number;
  status: string;
  message?: string;
  totalSubJobs?: number;
  totalSubJobsCompleted?: number;
  totalSubJobsFailed?: number;
  totalSubJobsInProgress?: number;
  totalSubJobsQueued?: number;
  startedAt?: string;
  completedAt?: string;
}

// Helper function to transform API responses
const transformStoreResponse = (response: StoreResponse): Store[] => {
  return response.stores.map(store => ({
    ...store,
    id: store.id || store.storeNumber,
    name: store.name || `Store ${store.storeNumber}`,
    isActive: store.isActive !== undefined ? store.isActive : true
  }));
};

// Axios-based API functions for Bulk Update
export const getAllStores = async (): Promise<Store[]> => {
  let allStores: Store[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get<StoreResponse>('/api/stores', { params: { page, pageSize } });
    const data = response.data;
    if (data && data.stores) {
      allStores = allStores.concat(transformStoreResponse(data));
      pages = data.pages || 1;
      page++;
    } else {
      break;
    }
  }
  return allStores;
};

// Get all store control records (store status, etc.). Returns the full array in
// one call — the /store-controls endpoint is not paginated.
export const getAllStoreControls = async (): Promise<StoreControlResponse[]> => {
  const response = await apiClient.get<StoreControlResponse[]>('/store-controls/');
  return Array.isArray(response.data) ? response.data : [];
};

// Get all modifier weights
export const getAllModifierWeights = async (): Promise<ModifierWeight[]> => {
  let all: ModifierWeight[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get('/api/modifiers/weights', { params: { page, pageSize } });
    const data = response.data;
    let items = Array.isArray(data) ? data : (data && Array.isArray(data.modifierWeights) ? data.modifierWeights : []);
    all = all.concat(items);
    pages = data.pages || 1;
    page++;
  }
  return all;
};

// Get all slots
export const getAllSlots = async (): Promise<Slot[]> => {
  let all: Slot[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get('/api/slots', { params: { page, pageSize } });
    const data = response.data;
    let items = Array.isArray(data) ? data : (data && Array.isArray(data.slots) ? data.slots : []);
    all = all.concat(items);
    pages = data.pages || 1;
    page++;
  }
  return all;
};

// Get all variants for a product
export const getAllVariants = async (productId: string): Promise<Variant[]> => {
  const response = await apiClient.post('/api/products/variants', { productId });
  if (Array.isArray(response.data)) return response.data;
  if (response.data && Array.isArray(response.data.variants)) return response.data.variants;
  return [];
};

// Transform function to normalize patch data from API response
const transformPatchData = (patch: any): any => {
  return {
    ...patch,
    // Map patchName to name for backwards compatibility
    name: patch.patchName || patch.name,
    // Operations are now flat - no GraphQL transformation needed
    operations: patch.operations || []
  };
};

export const getPatches = async (filters: any): Promise<any> => {
  // New API accepts single storeNumber, so we need to make parallel requests for multiple stores
  const { storeNumbers, ...otherFilters } = filters;
  
  // If storeNumbers is a string (comma-separated), convert to array
  const storeNumbersArray = typeof storeNumbers === 'string' 
    ? storeNumbers.split(',').filter(Boolean)
    : Array.isArray(storeNumbers) ? storeNumbers : [];
    
  if (storeNumbersArray.length === 0) {
    return {
      patches: [],
      count: 0,
      page: 0,
      pageSize: 0,
      pages: 0
    };
  }
  
  const tasks = storeNumbersArray.map((storeNumber: string) => async () => {
    const params = {
      ...otherFilters,
      storeNumber: storeNumber.trim()
    };
    
    try {
      const response = await apiClient.get('/api/patches', { params });
      const data = response.data;
      
      if (data && data.patches) {
        return data.patches.map((patch: any) => transformPatchData({
          ...patch,
          storeNumber
        }));
      }
      return [];
    } catch (error) {
      console.warn(`Failed to fetch patches for store ${storeNumber}:`, error);
      return [];
    }
  });
  
  const storeResults = await promisePool(tasks, 10);
  
  // Flatten all patches from all stores
  const allPatches = storeResults.flat();
  
  // Return aggregated data
  return {
    patches: allPatches,
    count: allPatches.length,
    page: 0,
    pageSize: allPatches.length,
    pages: 1
  };
};

export const createPatch = async (patch: any): Promise<any> => {
  const response = await apiClient.post('/api/patches', patch);
  return response.data;
};

export const submitPatchJobs = async (request: any, userId: string): Promise<any> => {
  const response = await apiClient.post('/bulk-pricing/patches/jobs', request, {
    headers: {
      'X-User-Id': userId,
    },
  });
  return response.data;
};

export const getPatchJobStatus = async (jobId: string): Promise<PatchJobStatusResponse> => {
  const response = await apiClient.get(`/bulk-pricing/patches/jobs/${jobId}/status`);
  return response.data;
};

export const getPatch = async (id: string): Promise<any> => {
  // Input validation
  if (!id || typeof id !== 'string' || id.trim().length === 0) {
    throw new Error('Invalid patch ID provided');
  }
  
  // Sanitize ID - only allow alphanumeric characters, hyphens, and underscores
  const sanitizedId = id.replace(/[^a-zA-Z0-9\-_]/g, '');
  if (sanitizedId !== id) {
    throw new Error('Patch ID contains invalid characters');
  }
  
  if (sanitizedId.length > 100) {
    throw new Error('Patch ID is too long');
  }
  
  const response = await apiClient.get(`/api/patches/${encodeURIComponent(sanitizedId)}`);
  return response.data;
};

export const deletePatch = async (id: string): Promise<any> => {
  const response = await apiClient.delete(`/api/patches/${id}`);
  return response.data;
};

export const getAllProducts = async (): Promise<any[]> => {
  let all: any[] = [];
  let page = 0;
  let pages = 1;
  const pageSize = 1000;
  while (page < pages) {
    const response = await apiClient.get('/api/products', { params: { page, pageSize } });
    const data = response.data;
    let items = Array.isArray(data) ? data : (data && Array.isArray(data.products) ? data.products : []);
    all = all.concat(items);
    pages = data.pages || 1;
    page++;
  }
  return all;
};


// Function to poll patch job status with configurable parameters
export const pollPatchJobStatus = async (
  jobId: string, 
  getStatus: (jobId: string) => Promise<PatchJobStatusResponse>,
  options: {
    pollingInterval?: number;  // in milliseconds, default 2000ms (2s)
    timeout?: number;          // in milliseconds, default 60000ms (60s)
    onProgress?: (status: PatchJobStatusResponse) => void;
    onComplete?: (status: PatchJobStatusResponse) => void;
    onError?: (error: any) => void;
  } = {}
): Promise<PatchJobStatusResponse> => {
  const {
    pollingInterval = 2000,
    timeout = 60000,
    onProgress,
    onComplete,
    onError
  } = options;

    const startTime = Date.now();
    let pollCount = 0;
    const maxPolls = Math.ceil(timeout / pollingInterval) + 100; // Failsafe

    // Create a promise that resolves when the job completes or fails
    return new Promise((resolve, reject) => {
      // Function to check status and recursively call itself
      const checkStatus = async () => {
        try {
          pollCount++;
          const status = await getStatus(jobId);
          // Log every poll for debugging
          // eslint-disable-next-line no-console
          console.log(`[pollPatchJobStatus] Poll #${pollCount} jobId=${jobId} status=`, status.status, status);
          if (onProgress) onProgress(status);

          // If job is complete or failed, resolve the promise
          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            if (onComplete) onComplete(status);
            resolve(status);
            return;
          }

          // If status is not recognized, log and break to avoid infinite loop
          const validStatuses = ['QUEUED', 'STARTED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];
          if (!validStatuses.includes(status.status)) {
            const err = new Error(`Unknown job status: ${status.status}`);
            console.error(`[pollPatchJobStatus] Unknown status for jobId=${jobId}:`, status);
            if (onError) onError(err);
            reject(err);
            return;
          }

          // If timeout exceeded, reject
          if (Date.now() - startTime > timeout) {
            const err = new Error('Polling timed out');
            if (onError) onError(err);
            reject(err);
            return;
          }

          // Failsafe: if too many polls, reject
          if (pollCount > maxPolls) {
            const err = new Error('Polling exceeded max allowed polls');
            if (onError) onError(err);
            reject(err);
            return;
          }

          // Only recurse asynchronously
          setTimeout(checkStatus, pollingInterval);
        } catch (error) {
          console.error('Error polling job status:', error);
          if (onError) onError(error);
          reject(error);
        }
      };

      // Start polling
      checkStatus();
    });
  };

// Function to poll multiple patch jobs in parallel and aggregate their status
export const pollMultiplePatchJobs = async (
  jobIds: string[],
  getStatus: (jobId: string) => Promise<PatchJobStatusResponse>,
  options: {
    pollingInterval?: number;
    timeout?: number;
    onProgress?: (aggregatedStatus: AggregatedPatchJobStatus) => void;
    onComplete?: (aggregatedStatus: AggregatedPatchJobStatus) => void;
    onError?: (error: any) => void;
  } = {}
): Promise<AggregatedPatchJobStatus> => {
  const {
    pollingInterval = 2000,
    onProgress,
    onComplete,
    onError
  } = options;

  if (!jobIds.length) {
    throw new Error('No job IDs provided');
  }

  const jobStatuses = new Map<string, PatchJobStatusResponse>();
  const completedJobs = new Set<string>();
  const failedJobs = new Set<string>();

  const pollAllJobs = async (): Promise<AggregatedPatchJobStatus> => {
    // Poll all jobs in parallel
    const promises = jobIds.map(async (jobId) => {
      if (completedJobs.has(jobId) || failedJobs.has(jobId)) {
        return jobStatuses.get(jobId)!;
      }

      try {
        const status = await getStatus(jobId);
        jobStatuses.set(jobId, status);

        if (status.status === 'COMPLETED') {
          completedJobs.add(jobId);
        } else if (status.status === 'FAILED') {
          failedJobs.add(jobId);
        }

        return status;
      } catch (error) {
        console.error(`Error polling job ${jobId}:`, error);
        failedJobs.add(jobId);
        // Create a failed status for this job
        const failedStatus: PatchJobStatusResponse = {
          success: false,
          jobId: parseInt(jobId),
          status: 'FAILED',
          message: error instanceof Error ? error.message : 'Unknown error',
          totalSubJobs: 0,
          totalSubJobsCompleted: 0,
          totalSubJobsFailed: 1,
          totalSubJobsInProgress: 0,
          totalSubJobsQueued: 0
        };
        jobStatuses.set(jobId, failedStatus);
        return failedStatus;
      }
    });

    const currentStatuses = await Promise.all(promises);
    
    // Aggregate the results
    const aggregated = aggregatePatchJobStatuses(currentStatuses, jobIds);
    
    if (onProgress) {
      onProgress(aggregated);
    }

    return aggregated;
  };

  // Start polling loop
  return new Promise((resolve, reject) => {
    const poll = async () => {
      try {
        const aggregated = await pollAllJobs();
        
        // Check if all jobs are completed or failed
        const allJobsFinished = jobIds.every(jobId => 
          completedJobs.has(jobId) || failedJobs.has(jobId)
        );

        if (allJobsFinished) {
          if (onComplete) onComplete(aggregated);
          resolve(aggregated);
          return;
        }

        // Continue polling
        setTimeout(poll, pollingInterval);
      } catch (error) {
        console.error('Error in polling loop:', error);
        if (onError) onError(error);
        reject(error);
      }
    };

    poll();
  });
};

export interface AggregatedPatchJobStatus {
  jobIds: string[];
  overallStatus: 'QUEUED' | 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  inProgressJobs: number;
  totalSubJobs: number;
  totalSubJobsCompleted: number;
  totalSubJobsFailed: number;
  totalSubJobsInProgress: number;
  totalSubJobsQueued: number;
  startedAt?: string;
  completedAt?: string;
  message?: string;
}

// Helper function to aggregate multiple patch job statuses
export const aggregatePatchJobStatuses = (
  statuses: PatchJobStatusResponse[],
  jobIds: string[]
): AggregatedPatchJobStatus => {
  const totalJobs = jobIds.length;
  let completedJobs = 0;
  let failedJobs = 0;
  let inProgressJobs = 0;
  
  let totalSubJobs = 0;
  let totalSubJobsCompleted = 0;
  let totalSubJobsFailed = 0;
  let totalSubJobsInProgress = 0;
  let totalSubJobsQueued = 0;
  
  let earliestStartedAt: string | undefined;
  let latestCompletedAt: string | undefined;
  const messages: string[] = [];

  statuses.forEach(status => {
    // Count job states
    if (status.status === 'COMPLETED') {
      completedJobs++;
    } else if (status.status === 'FAILED') {
      failedJobs++;
    } else if (['QUEUED', 'STARTED', 'IN_PROGRESS'].includes(status.status)) {
      inProgressJobs++;
    }

    // Since backend doesn't return sub-jobs info, treat each job as 1 sub-job
    totalSubJobs += 1;
    if (status.status === 'COMPLETED') {
      totalSubJobsCompleted += 1;
    } else if (status.status === 'FAILED') {
      totalSubJobsFailed += 1;
    } else if (['QUEUED', 'STARTED', 'IN_PROGRESS'].includes(status.status)) {
      totalSubJobsInProgress += 1;
    }

    // Track timing
    if (status.startedAt) {
      if (!earliestStartedAt || status.startedAt < earliestStartedAt) {
        earliestStartedAt = status.startedAt;
      }
    }
    
    if (status.completedAt) {
      if (!latestCompletedAt || status.completedAt > latestCompletedAt) {
        latestCompletedAt = status.completedAt;
      }
    }

    // Collect error messages
    if (status.message && status.status === 'FAILED') {
      messages.push(`Job ${status.jobId}: ${status.message}`);
    }
  });

  // Determine overall status
  let overallStatus: 'QUEUED' | 'STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  if (failedJobs > 0 && completedJobs + failedJobs === totalJobs) {
    // All jobs finished, but some failed
    overallStatus = 'FAILED';
  } else if (completedJobs === totalJobs) {
    // All jobs completed successfully
    overallStatus = 'COMPLETED';
  } else if (inProgressJobs > 0) {
    // Some jobs are still running
    overallStatus = 'IN_PROGRESS';
  } else {
    // Default to queued if unclear
    overallStatus = 'QUEUED';
  }

  return {
    jobIds,
    overallStatus,
    totalJobs,
    completedJobs,
    failedJobs,
    inProgressJobs,
    totalSubJobs,
    totalSubJobsCompleted,
    totalSubJobsFailed,
    totalSubJobsInProgress,
    totalSubJobsQueued,
    startedAt: earliestStartedAt,
    completedAt: latestCompletedAt,
    message: messages.length > 0 ? messages.join('; ') : undefined
  };
};

export { apiClient };
